import { Link } from "react-router-dom";
import {
  ArrowRight,
  FileSearch,
  BookOpen,
  HelpCircle,
  FileText,
  Newspaper,
  Sparkles,
  ShieldCheck,
  Download,
} from "lucide-react";
import { brand } from "@/data/site/brand";

interface Props {
  onClose: () => void;
}

const resources = [
  {
    icon: Download,
    name: "Free Guide: 7 IRS Secrets",
    description:
      "Free 23-page CPA-written PDF — settlement options, statute expiration, penalty abatement, and the programs the IRS never advertises.",
    href: "/resources/7-secrets-guide",
  },
  {
    icon: FileText,
    name: "IRS Notice Library",
    description:
      "CP14, CP504, LT11, CP2000 and every other IRS notice — deadlines, consequences, exact next steps.",
    href: "/resources/irs-notices",
  },
  {
    icon: FileText,
    name: "IRS Forms Library",
    description:
      "Form 433-A, 433-D, 656, 9465 and more — how to file each one without getting trapped.",
    href: "/resources/irs-forms",
  },
  {
    icon: BookOpen,
    name: "Tax & IRS Glossary",
    description: "Plain-English reference of tax terms, acronyms, and IRS definitions.",
    href: "/resources/glossary",
  },
  {
    icon: HelpCircle,
    name: "Frequently Asked Questions",
    description: "Answers to common questions about IRS issues and tax resolution.",
    href: "/faq",
  },
  {
    icon: Newspaper,
    name: "Blog & Case Studies",
    description: "Strategy, walkthroughs, and updates from our CPA-led resolution team.",
    href: "/blog",
  },
  {
    icon: BookOpen,
    name: "How It Works",
    description: "Our 3-step process from first call to IRS resolution.",
    href: "/how-it-works",
  },
];

export const SiteResourcesDropdown = ({ onClose }: Props) => {
  return (
    <div className="bg-white border-b border-border shadow-xl">
      <div className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-12 gap-6">
          {/* Featured tool */}
          <Link
            to="/resources/notice-lookup"
            onClick={onClose}
            className="col-span-12 lg:col-span-4 group relative overflow-hidden rounded-xl bg-gradient-navy text-white p-6 flex flex-col justify-between hover:shadow-xl transition-shadow"
          >
            <div
              className="absolute inset-0 opacity-[0.08] pointer-events-none"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 20% 20%, hsl(var(--gold)) 1px, transparent 1px)",
                backgroundSize: "28px 28px",
              }}
            />
            <div className="relative">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white/80 mb-3">
                <Sparkles className="w-3 h-3" /> Featured Tool
              </span>
              <h3 className="font-heading text-xl font-bold leading-tight">
                IRS Notice Lookup
              </h3>
              <p className="text-[13px] text-white/75 mt-2 leading-snug">
                Type your notice number — CP14, LT11, CP2000 — and get the deadline,
                consequences, and your exact next move in about 10 seconds.
              </p>
            </div>
            <div className="relative mt-5 flex items-center gap-3 rounded-lg bg-white/10 px-3 py-2.5 group-hover:bg-white/15 transition-colors">
              <FileSearch className="w-5 h-5 text-gold shrink-0" />
              <span className="text-sm font-semibold flex-1">Look up your notice</span>
              <ArrowRight className="w-4 h-4 text-gold group-hover:translate-x-0.5 transition-transform" />
            </div>
          </Link>

          {/* Resource grid */}
          <div className="col-span-12 lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-1">
            {resources.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={onClose}
                className="group flex gap-3 p-3 rounded-lg hover:bg-secondary/60 transition-colors"
              >
                <div className="shrink-0 w-10 h-10 rounded-lg bg-secondary flex items-center justify-center text-navy group-hover:bg-gold group-hover:text-navy-dark transition-colors">
                  <item.icon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-bold text-navy group-hover:text-navy-dark transition-colors leading-tight">
                    {item.name}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1 leading-snug line-clamp-2">
                    {item.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-6 pt-5 border-t border-border flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="w-4 h-4 text-gold" />
            Written and reviewed by{" "}
            <strong className="text-navy">CPAs with {brand.yearsExperience}+ years</strong>{" "}
            of IRS controversy experience.
          </div>
          <Link
            to="/free-consultation"
            onClick={onClose}
            className="inline-flex items-center gap-2 text-sm font-semibold text-navy uppercase tracking-wider hover:text-navy-dark transition-colors"
          >
            Free Case Review <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};
